import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, RotateCcw, Loader2, Workflow, Mic2 } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { InstitutionStyledTemporalInput } from '@/components/institution/InstitutionStyledTemporalInput';
import { useAuthStore } from '@/stores/auth.store';

type TrashFlow = {
  id: string;
  title: string;
  description?: string | null;
  deletedAt: string | null;
  updatedAt: string;
  teacher?: { id: string; email: string; name: string };
};

type TrashPodcast = {
  id: string;
  title: string;
  description?: string;
  deletedAt: string | null;
  updatedAt: string;
  createdByName?: string | null;
  assignedProducerName?: string | null;
};

type PurgeMode = 'all' | 'range';

export default function InstitutionTrashPanel() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [flows, setFlows] = useState<TrashFlow[]>([]);
  const [podcasts, setPodcasts] = useState<TrashPodcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeMode, setPurgeMode] = useState<PurgeMode>('all');
  const [purgeDateFrom, setPurgeDateFrom] = useState('');
  const [purgeDateTo, setPurgeDateTo] = useState('');
  const [purging, setPurging] = useState(false);

  const canPurgeTrash = useMemo(() => {
    const r = user?.role != null ? String(user.role).toLowerCase().trim() : '';
    return (
      r === 'institution_admin' ||
      r === 'institution' ||
      r === 'drh' ||
      r === 'rh' ||
      r === 'resp_formation'
    );
  }, [user?.role]);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const raw: any = await api.get('/institution/trash');
      const inner = raw?.data ?? raw;
      setFlows(Array.isArray(inner?.flows) ? inner.flows : []);
      setPodcasts(Array.isArray(inner?.podcasts) ? inner.podcasts : []);
    } catch {
      setFlows([]);
      setPodcasts([]);
      setMessage({ type: 'err', text: t('institution.trash.loadError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const openPurgeModal = () => {
    setPurgeMode('all');
    setPurgeDateFrom('');
    setPurgeDateTo('');
    setPurgeOpen(true);
    setMessage(null);
  };

  const runPurge = async () => {
    if (purgeMode === 'range') {
      if (!purgeDateFrom.trim() || !purgeDateTo.trim()) {
        setMessage({ type: 'err', text: t('institution.trash.purgeRangeMissingDates') });
        return;
      }
      if (purgeDateFrom > purgeDateTo) {
        setMessage({ type: 'err', text: t('institution.trash.purgeRangeOrderError') });
        return;
      }
    }
    setPurging(true);
    setMessage(null);
    try {
      const body =
        purgeMode === 'all'
          ? { mode: 'all' as const }
          : { mode: 'range' as const, dateFrom: purgeDateFrom.trim(), dateTo: purgeDateTo.trim() };
      await api.post('/institution/trash/purge', body);
      setPurgeOpen(false);
      setMessage({ type: 'ok', text: t('institution.trash.purgeSuccess') });
      await load();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Erreur';
      setMessage({ type: 'err', text: msg });
    } finally {
      setPurging(false);
    }
  };

  const restoreFlow = async (id: string) => {
    setRestoringId(`flow:${id}`);
    setMessage(null);
    try {
      await api.post(`/institution/trash/flows/${id}/restore`, {});
      setFlows((prev) => prev.filter((f) => f.id !== id));
      setMessage({ type: 'ok', text: t('institution.trash.restoredFlow') });
    } catch {
      setMessage({ type: 'err', text: t('institution.trash.restoreError') });
    } finally {
      setRestoringId(null);
    }
  };

  const restorePodcast = async (id: string) => {
    setRestoringId(`pod:${id}`);
    setMessage(null);
    try {
      await api.post(`/institution/trash/podcasts/${id}/restore`, {});
      setPodcasts((prev) => prev.filter((p) => p.id !== id));
      setMessage({ type: 'ok', text: t('institution.trash.restoredPodcast') });
    } catch {
      setMessage({ type: 'err', text: t('institution.trash.restoreError') });
    } finally {
      setRestoringId(null);
    }
  };

  const empty = !loading && flows.length === 0 && podcasts.length === 0;
  const hasItems = flows.length > 0 || podcasts.length > 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
            <Trash2 className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.tabs.trash')}</h1>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.trash.subtitle')}</p>
          </div>
        </div>
        {canPurgeTrash && hasItems && !loading && (
          <button
            type="button"
            onClick={openPurgeModal}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-800 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-950/60"
          >
            <Trash2 className="h-4 w-4" />
            {t('institution.trash.purgeButton')}
          </button>
        )}
      </div>

      {message && (
        <div
          className={cn(
            'rounded-lg border px-4 py-3 text-sm',
            message.type === 'ok'
              ? 'border-[#B6CAEC] bg-[#F0F4FA] text-[#0C1C38] dark:border-[#F8FAFC]/40 dark:bg-[#F8FAFC]/20 dark:text-[#19335E]'
              : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200',
          )}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#5B6472]">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('institution.trash.loading')}
        </div>
      ) : empty ? (
        <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.trash.empty')}</p>
      ) : (
        <div className="space-y-10">
          {flows.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0C1C38] dark:text-white">
                <Workflow className="h-4 w-4" />
                {t('institution.trash.sectionFlows')}
              </h2>
              <ul className="divide-y divide-[#E8EDF5] rounded-xl border border-[#E8EDF5] dark:divide-[#19335E] dark:border-[#19335E]">
                {flows.map((f) => (
                  <li
                    key={f.id}
                    className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium text-[#0C1C38] dark:text-white">{f.title}</p>
                      {f.teacher?.name && (
                        <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                          {t('institution.trash.owner')}: {f.teacher.name}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={restoringId !== null}
                      onClick={() => void restoreFlow(f.id)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm font-medium text-[#121826] hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-[#DCE7F7] dark:hover:bg-[#19335E]"
                    >
                      {restoringId === `flow:${f.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4" />
                      )}
                      {t('institution.trash.restore')}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {podcasts.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0C1C38] dark:text-white">
                <Mic2 className="h-4 w-4" />
                {t('institution.trash.sectionPodcasts')}
              </h2>
              <ul className="divide-y divide-[#E8EDF5] rounded-xl border border-[#E8EDF5] dark:divide-[#19335E] dark:border-[#19335E]">
                {podcasts.map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium text-[#0C1C38] dark:text-white">{p.title}</p>
                      {(p.assignedProducerName || p.createdByName) && (
                        <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                          {[p.assignedProducerName, p.createdByName].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={restoringId !== null}
                      onClick={() => void restorePodcast(p.id)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#E8EDF5] bg-white px-3 py-2 text-sm font-medium text-[#121826] hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-[#DCE7F7] dark:hover:bg-[#19335E]"
                    >
                      {restoringId === `pod:${p.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4" />
                      )}
                      {t('institution.trash.restore')}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      <Modal
        open={purgeOpen}
        onClose={() => {
          if (!purging) setPurgeOpen(false);
        }}
        size="md"
        closeOnBackdrop={!purging}
        ariaLabelledBy="trash-purge-title"
      >
        <ModalHeader>
          <h3 id="trash-purge-title" className="text-base font-semibold text-[#0C1C38] dark:text-white">
            {t('institution.trash.purgeModalTitle')}
          </h3>
        </ModalHeader>
        <ModalBody>
          <p className="mb-4 text-sm text-[#5B6472] dark:text-[#DCE7F7]">{t('institution.trash.purgeModalHint')}</p>
          <fieldset className="space-y-3">
            <legend className="sr-only">{t('institution.trash.purgeScopeLabel')}</legend>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#E8EDF5] p-3 dark:border-[#19335E]">
              <input
                type="radio"
                name="purgeMode"
                className="mt-1"
                checked={purgeMode === 'all'}
                onChange={() => setPurgeMode('all')}
                disabled={purging}
              />
              <span>
                <span className="block text-sm font-medium text-[#0C1C38] dark:text-white">
                  {t('institution.trash.purgeAllLabel')}
                </span>
                <span className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.trash.purgeAllDesc')}</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#E8EDF5] p-3 dark:border-[#19335E]">
              <input
                type="radio"
                name="purgeMode"
                className="mt-1"
                checked={purgeMode === 'range'}
                onChange={() => setPurgeMode('range')}
                disabled={purging}
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-[#0C1C38] dark:text-white">
                  {t('institution.trash.purgeRangeLabel')}
                </span>
                <span className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.trash.purgeRangeDesc')}</span>
                {purgeMode === 'range' && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <label className="flex-1 text-xs">
                      <span className="mb-1 block font-medium text-[#121826] dark:text-[#DCE7F7]">
                        {t('institution.trash.purgeDateFrom')}
                      </span>
                      <InstitutionStyledTemporalInput
                        variant="date"
                        value={purgeDateFrom}
                        onChange={(e) => setPurgeDateFrom(e.target.value)}
                        disabled={purging}
                      />
                    </label>
                    <label className="flex-1 text-xs">
                      <span className="mb-1 block font-medium text-[#121826] dark:text-[#DCE7F7]">
                        {t('institution.trash.purgeDateTo')}
                      </span>
                      <InstitutionStyledTemporalInput
                        variant="date"
                        value={purgeDateTo}
                        onChange={(e) => setPurgeDateTo(e.target.value)}
                        disabled={purging}
                      />
                    </label>
                  </div>
                )}
              </span>
            </label>
          </fieldset>
        </ModalBody>
        <ModalFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setPurgeOpen(false)}
            disabled={purging}
            className="w-full rounded-xl border border-[#E8EDF5] bg-white px-4 py-2 text-sm font-semibold text-[#121826] hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-[#DCE7F7] dark:hover:bg-[#19335E] sm:w-auto"
          >
            {t('btn.cancel', 'Annuler')}
          </button>
          <button
            type="button"
            onClick={() => void runPurge()}
            disabled={purging}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 sm:w-auto"
          >
            {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('institution.trash.purgeConfirm')}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
